import {
  CANVAS_ACTION_CARD_HEIGHT_MESSAGE_TYPE,
  CANVAS_ACTION_CARD_SLOTS_MESSAGE_TYPE,
  parseCanvasActionCardSlotsMessage,
  type CanvasActionCardReference,
  type CanvasActionCardSlot,
} from '@littlebird/canvas';
import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

type SlotReport = {
  capability: string;
  slots: CanvasActionCardSlot[];
};

export const useCanvasActionCards = ({
  iframeRef,
  bridgeCapability,
  enabled,
  references,
}: {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  bridgeCapability: string;
  enabled: boolean;
  references: CanvasActionCardReference[];
}) => {
  const [slotReport, setSlotReport] = useState<SlotReport | null>(null);
  const allowedLocalIds = useMemo(
    () => new Set(references.map(({ localId }) => localId)),
    [references]
  );
  const slots = useMemo(
    () => (slotReport?.capability === bridgeCapability ? slotReport.slots : []),
    [bridgeCapability, slotReport]
  );
  const hasSlotReport = slotReport?.capability === bridgeCapability;

  useLayoutEffect(() => {
    if (!enabled) return;

    const handleMessage = (event: MessageEvent) => {
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow) return;
      const nextSlots = parseCanvasActionCardSlotsMessage(
        event.data,
        bridgeCapability,
        allowedLocalIds,
        { width: iframe.clientWidth, height: iframe.clientHeight }
      );
      if (nextSlots !== null) {
        setSlotReport({ capability: bridgeCapability, slots: nextSlots });
        return;
      }

      const data = event.data as Record<string, unknown> | null;
      if (
        data?.type === CANVAS_ACTION_CARD_SLOTS_MESSAGE_TYPE &&
        data?.capability === bridgeCapability
      ) {
        setSlotReport({ capability: bridgeCapability, slots: [] });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [allowedLocalIds, bridgeCapability, enabled, iframeRef]);

  const handleHeightChange = useCallback(
    (slotId: string, height: number) => {
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: CANVAS_ACTION_CARD_HEIGHT_MESSAGE_TYPE,
          capability: bridgeCapability,
          slotId,
          height,
        },
        '*'
      );
    },
    [bridgeCapability, iframeRef]
  );

  const fallbackLocalIds = hasSlotReport
    ? []
    : references.map(({ localId }) => localId);

  return { slots, fallbackLocalIds, handleHeightChange };
};
